import { ProductoPublico, EmpresaPublica } from '../types/catalogo';
import { formatearPrecio } from './formateo';

function empresaPaisCodigo(empresa: Pick<EmpresaPublica, 'pais'>): string {
  return normalizarPaisACodigo(empresa.pais || '');
}

function esEmpresaVenezuela(empresa: Pick<EmpresaPublica, 'pais'>): boolean {
  return empresaPaisCodigo(empresa) === 'VE';
}

function mensajeCierreWhatsApp(empresa: Pick<EmpresaPublica, 'pais'>): string {
  if (esEmpresaVenezuela(empresa)) {
    return '\n\n¿Me confirmas disponibilidad y forma de pago?\n\n¡Gracias! 🙏';
  }
  return '\n\n¿Podrías darme más información sobre este producto?\n\nGracias! 😊';
}

function mensajeBienvenidaDefault(empresa: Pick<EmpresaPublica, 'pais' | 'nombre'>): string {
  if (esEmpresaVenezuela(empresa)) {
    return '¡Hola! Me interesa este producto de tu catálogo:';
  }
  return '¡Hola! Me interesa este producto:';
}

/**
 * Genera un mensaje pre-formateado para WhatsApp basado en el producto y la empresa
 */
export const generarMensajeWhatsApp = (
  producto: ProductoPublico, 
  empresa: EmpresaPublica,
  configuracion?: any
): string => {
  const paisCodigo = empresaPaisCodigo(empresa);
  const mensajeBienvenida = configuracion?.mensajeBienvenida || mensajeBienvenidaDefault(empresa);
  
  let mensaje = mensajeBienvenida;
  
  // Información del producto
  mensaje += `\n\n📱 *${producto.nombre}*`;
  
  if (producto.descripcion) {
    mensaje += `\n📝 ${producto.descripcion}`;
  }
  
  // Precio (si está configurado para mostrar)
  if (producto.mostrarPrecio && configuracion?.mostrarPrecios !== false) {
    mensaje += `\n💰 Precio: ${formatearPrecio(producto.precio, paisCodigo)}`;
  }
  
  // Stock (si está configurado para mostrar)
  if (producto.mostrarStock && producto.stock > 0) {
    const textoStock = configuracion?.mostrarStockExacto 
      ? `${producto.stock} disponibles`
      : 'Disponible';
    mensaje += `\n📦 Stock: ${textoStock}`;
  }
  
  // Información adicional
  if (producto.tiempoPreparacion) {
    mensaje += `\n⏱️ Tiempo: ${producto.tiempoPreparacion}`;
  }
  
  if (producto.ubicacion) {
    mensaje += `\n📍 Ubicación: ${producto.ubicacion}`;
  }
  
  // Tags/categorías
  if (producto.tags && producto.tags.length > 0) {
    mensaje += `\n🏷️ Categoría: ${producto.tags.join(', ')}`;
  }
  
  // Imagen del producto
  const imagenPrincipal = producto.imagenes.find(img => img.esPrincipal) || producto.imagenes[0];
  if (imagenPrincipal?.url) {
    mensaje += `\n\n🖼️ Ver imagen: ${imagenPrincipal.urlOptimizada || imagenPrincipal.url}`;
  }
  
  // Cierre del mensaje
  mensaje += mensajeCierreWhatsApp(empresa);
  
  return mensaje;
};

/**
 * Genera la URL de WhatsApp con el mensaje pre-formateado
 */
export const generarUrlWhatsApp = (
  numero: string,
  mensaje: string,
  defaultCountryCode: string = '57'
): string => {
  const digitsOnly = formatPhoneNumberForWhatsApp(numero, defaultCountryCode);
  if (!digitsOnly) return '';
  const mensajeCodificado = encodeURIComponent(mensaje);
  return `https://wa.me/${digitsOnly}?text=${mensajeCodificado}`;
};

/**
 * Abre WhatsApp en una nueva ventana/pestaña
 */
export const abrirWhatsApp = (
  numero: string, 
  mensaje: string,
  tracking?: {
    productoId: string;
    empresaId: string;
    productoNombre: string;
  }
): void => {
  const url = generarUrlWhatsApp(numero, mensaje);
  if (!url) {
    if (typeof window !== 'undefined') {
      window.alert('No se pudo abrir WhatsApp: número no válido.');
    }
    return;
  }

  // Tracking de analytics (si está disponible)
  if (tracking && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'whatsapp_click', {
      product_id: tracking.productoId,
      empresa_id: tracking.empresaId,
      product_name: tracking.productoNombre
    });
  }
  
  // Abrir WhatsApp
  window.open(url, '_blank');
};

/**
 * Genera un mensaje para múltiples productos
 */
export const generarMensajeMultiplesProductos = (
  productos: ProductoPublico[], 
  empresa: EmpresaPublica,
  configuracion?: any
): string => {
  const paisCodigo = empresaPaisCodigo(empresa);
  let mensaje = configuracion?.mensajeBienvenida || mensajeBienvenidaDefault(empresa);
  
  productos.forEach((producto, index) => {
    mensaje += `\n\n${index + 1}. 📱 *${producto.nombre}*`;
    
    if (producto.descripcion) {
      mensaje += `\n   📝 ${producto.descripcion}`;
    }
    
    if (producto.mostrarPrecio && configuracion?.mostrarPrecios !== false) {
      mensaje += `\n   💰 ${formatearPrecio(producto.precio, paisCodigo)}`;
    }
  });
  
  mensaje += esEmpresaVenezuela(empresa)
    ? `\n\n¿Me confirmas disponibilidad de estos productos?\n\n¡Gracias! 🙏`
    : `\n\n¿Podrías darme información sobre estos productos?\n\nGracias! 😊`;
  
  return mensaje;
};

/**
 * Genera un mensaje para consulta general
 */
export const generarMensajeConsultaGeneral = (
  empresa: EmpresaPublica,
  configuracion?: any
): string => {
  let mensaje = '¡Hola! ';
  const ve = esEmpresaVenezuela(empresa);
  
  if (configuracion?.mensajeBienvenida) {
    mensaje += configuracion.mensajeBienvenida;
  } else if (ve) {
    mensaje += `Quisiera consultar sobre los productos de ${empresa.nombre}`;
  } else {
    mensaje += `Me gustaría hacer una consulta sobre los productos de ${empresa.nombre}`;
  }
  
  mensaje += ve
    ? `\n\n¿Me puedes ayudar?\n\n¡Gracias! 🙏`
    : `\n\n¿Podrías ayudarme?\n\nGracias! 😊`;
  
  return mensaje;
};

/** Map country name or ISO code to ISO 3166-1 alpha-2 for flags and dial codes. */
export function normalizarPaisACodigo(pais: string): string {
  const raw = String(pais || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (upper.length === 2) return upper;
  const byName: Record<string, string> = {
    COLOMBIA: 'CO',
    MÉXICO: 'MX',
    MEXICO: 'MX',
    ARGENTINA: 'AR',
    PERÚ: 'PE',
    PERU: 'PE',
    CHILE: 'CL',
    ECUADOR: 'EC',
    VENEZUELA: 'VE',
    BOLIVIA: 'BO',
    PARAGUAY: 'PY',
    URUGUAY: 'UY',
    BRASIL: 'BR',
    BRAZIL: 'BR',
  };
  return byName[upper] || upper.slice(0, 2);
}

/** Map country code (CO, MX, US, etc.) to WhatsApp dial prefix (digits only). */
export const getCountryDialCode = (pais?: string): string => {
  const code = normalizarPaisACodigo(pais || '');
  const map: Record<string, string> = {
    CO: '57', MX: '52', AR: '54', PE: '51', CL: '56', EC: '593', VE: '58',
    US: '1', CA: '1', BR: '55', ES: '34', UK: '44', IT: '39', DE: '49', FR: '33',
    BO: '591', PY: '595', UY: '598',
  };
  return (code && map[code]) || '57';
};

/**
 * Format phone for wa.me URL: digits only, with country code.
 * If the number starts with + (e.g. +1 602..., +52 55..., +57 300...), the digits are used as-is.
 * Otherwise defaultCountryCode is prepended (e.g. 57 for Colombia).
 */
export const formatPhoneNumberForWhatsApp = (
  phone: string,
  defaultCountryCode: string = '57'
): string => {
  if (!phone || !phone.trim()) return '';
  const trimmed = phone.trim();
  const cleanDigits = trimmed.replace(/\D/g, '');
  if (!cleanDigits) return '';
  // User included country code (number started with +)
  if (/^\s*\+/.test(trimmed)) return cleanDigits;
  // Prepend default country code only when the number did not start with +
  const defaultDigits = defaultCountryCode.replace(/\D/g, '') || '57';
  return defaultDigits + cleanDigits;
};

/**
 * Valida si un número de WhatsApp es válido
 */
export const validarNumeroWhatsApp = (numero: string): boolean => {
  // Patrón básico para números de WhatsApp
  const patron = /^(\+?[1-9]\d{1,14})$/;
  return patron.test(numero.replace(/[\s\-()]/g, ''));
};

/**
 * Formatea un número de teléfono para mostrar
 */
export const formatearTelefono = (numero: string, pais: string = 'CO'): string => {
  const numeroLimpio = numero.replace(/[\s\-()]/g, '');
  
  // Formateo específico por país
  switch (pais) {
    case 'CO':
      if (numeroLimpio.startsWith('+57')) {
        return `+57 ${numeroLimpio.slice(3, 5)} ${numeroLimpio.slice(5, 8)} ${numeroLimpio.slice(8)}`;
      } else if (numeroLimpio.startsWith('57')) {
        return `+57 ${numeroLimpio.slice(2, 4)} ${numeroLimpio.slice(4, 7)} ${numeroLimpio.slice(7)}`;
      } else if (numeroLimpio.startsWith('3')) {
        return `+57 ${numeroLimpio.slice(0, 2)} ${numeroLimpio.slice(2, 5)} ${numeroLimpio.slice(5)}`;
      }
      break;
      
    case 'MX':
      if (numeroLimpio.startsWith('+52')) {
        return `+52 ${numeroLimpio.slice(3, 6)} ${numeroLimpio.slice(6, 10)} ${numeroLimpio.slice(10)}`;
      }
      break;
      
    case 'AR':
      if (numeroLimpio.startsWith('+54')) {
        return `+54 ${numeroLimpio.slice(3, 5)} ${numeroLimpio.slice(5, 9)} ${numeroLimpio.slice(9)}`;
      }
      break;
  }
  
  // Formato genérico si no coincide con ningún patrón específico
  return numeroLimpio;
};

/**
 * Formatea un número de teléfono para mostrar (alias para compatibilidad)
 */
export const formatearNumeroTelefono = formatearTelefono;

/**
 * Obtiene el emoji de bandera para un país
 */
export const obtenerEmojiBandera = (pais: string): string => {
  const normalized = normalizarPaisACodigo(pais);
  const banderas: Record<string, string> = {
    'CO': '🇨🇴',
    'MX': '🇲🇽',
    'AR': '🇦🇷',
    'PE': '🇵🇪',
    'CL': '🇨🇱',
    'EC': '🇪🇨',
    'VE': '🇻🇪',
    'BO': '🇧🇴',
    'PY': '🇵🇾',
    'UY': '🇺🇾',
    'BR': '🇧🇷'
  };
  
  return banderas[normalized] || '🌍';
};

/**
 * Genera un mensaje de error para WhatsApp no disponible
 */
export const generarMensajeErrorWhatsApp = (empresa: EmpresaPublica): string => {
  return `Lo sentimos, ${empresa.nombre} no tiene WhatsApp configurado en este momento. ` +
         `Por favor contacta por teléfono: ${empresa.telefono || 'No disponible'} o ` +
         `email: ${empresa.email || 'No disponible'}`;
};

/**
 * Dynamic WhatsApp Link Generator for Client Catalogs
 * Generates WhatsApp links with client-specific phone numbers and custom message templates
 */
export const generateWhatsAppLink = (
  client: { whatsapp_number?: string; whatsapp_message_template?: string },
  productName: string = '',
  productPrice: string = '',
  productImageUrl?: string,
  /** e.g. public product deep link */
  productPageUrl?: string,
  pais?: string
): string | null => {
  if (!client.whatsapp_number) return null;

  const dialCode = getCountryDialCode(pais);
  const formattedNumber = formatPhoneNumberForWhatsApp(client.whatsapp_number, dialCode);
  if (!formattedNumber) return null;

  let message = client.whatsapp_message_template ||
    `Hola, estoy interesado en ${productName ? productName : 'sus productos'}`;

  if (productName && productPrice) {
    message += ` (${productName} - ${productPrice})`;
  }

  // Add product image URL if available
  if (productImageUrl) {
    message += `\n\n🖼️ Ver imagen del producto: ${productImageUrl}`;
  }

  if (productPageUrl) {
    message += `\n\n🔗 ${productPageUrl}`;
  }

  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedNumber}?text=${encodedMessage}`;
};

/**
 * Opens WhatsApp with client-specific link
 */
export const openWhatsAppClient = (
  client: { whatsapp_number?: string; whatsapp_message_template?: string },
  productName: string = '',
  productPrice: string = '',
  productImageUrl?: string,
  productPageUrl?: string
): void => {
  const link = generateWhatsAppLink(
    client,
    productName,
    productPrice,
    productImageUrl,
    productPageUrl
  );
  if (link) {
    window.open(link, '_blank');
  }
};

/**
 * Validates WhatsApp number format (international format, numbers only)
 */
export const validateWhatsAppNumber = (number: string): boolean => {
  // Remove all non-numeric characters
  const cleanNumber = number.replace(/\D/g, '');
  
  // Check if it's a valid international format (7-15 digits)
  return /^[1-9]\d{6,14}$/.test(cleanNumber);
};

/**
 * Formats WhatsApp number for display (adds + prefix)
 */
export const formatWhatsAppNumber = (number: string): string => {
  const cleanNumber = number.replace(/\D/g, '');
  return cleanNumber ? `+${cleanNumber}` : '';
};

/**
 * Cleans WhatsApp number input (removes non-numeric characters)
 */
export const cleanWhatsAppNumber = (input: string): string => {
  return input.replace(/\D/g, '');
};
